import React, { useEffect } from 'react';
import { X, Printer } from 'lucide-react';

const BusCardPrintModal = ({ enrollment, onClose, onPrinted }) => {
  useEffect(() => {
    // Add print styles
    const style = document.createElement('style');
    style.id = 'bus-card-print-styles';
    style.textContent = `
      @media print {
        @page {
          size: A4 landscape;
          margin: 0;
        }
        
        /* Hide modal and everything else */
        .fixed.inset-0,
        body > *:not(#bus-card-print-area) {
          display: none !important;
        }
        
        /* Show only print area */
        #bus-card-print-area {
          display: block !important;
          position: fixed !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Build full name: FirstName + FatherName (FatherName already includes family name)
    const getFullStudentName = () => {
      console.log('Name parts:', {
        FirstName: enrollment.FirstName,
        FatherName: enrollment.FatherName,
        LastName: enrollment.LastName,
        StudentName: enrollment.StudentName
      });
      const parts = [];
      if (enrollment.FirstName) parts.push(enrollment.FirstName);
      if (enrollment.FatherName) parts.push(enrollment.FatherName);
      // Don't add LastName - it's already in FatherName
      const fullName = parts.filter(Boolean).join(' ');
      console.log('Built full name:', fullName);
      return fullName;
    };

    // Create print area in body
    const printArea = document.createElement('div');
    printArea.id = 'bus-card-print-area';
    printArea.style.display = 'none';
    printArea.innerHTML = `
      <div style="width: 297mm; height: 210mm; display: flex; align-items: center; justify-content: flex-end; padding-right: 1mm; background-color: white;">
        <div style="width: 133mm; height: 90mm; padding: 8mm; font-family: Arial, sans-serif;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6mm;">
            <div style="flex: 1; text-align: center;">
              <p style="font-size: 14px; font-weight: bold; margin: 0; line-height: 1.3;">Collège des Apôtres</p>
              <p style="font-size: 14px; font-weight: bold; margin: 0; line-height: 1.3;">Jounieh</p>
            </div>
            <div style="flex: 1; text-align: center;">
              <p style="font-size: 20px; font-weight: bold; margin: 0;">Autocar</p>
            </div>
            <div style="flex: 1; text-align: center;">
              ${getAcademicYearShort() ? `<p style="font-size: 14px; font-weight: bold; margin: 0; line-height: 1.3;">${getAcademicYearShort()}</p>` : ''}
              <p style="font-size: 14px; font-weight: bold; margin: 0; line-height: 1.3;">${getTrimesterDisplay()}</p>
            </div>
          </div>
          
          <div style="border: 2px solid black; padding: 6px 10px; margin-bottom: 5mm;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <div style="flex: 1;">
                <div style="margin-bottom: 6px;">
                  <span style="font-size: 12px;">Élève: </span>
                  <span style="font-size: 12px; font-weight: bold;">${getFullStudentName()}</span>
                </div>
                <div>
                  <span style="font-size: 12px; font-weight: bold;">Tel: </span>
                  <span style="font-size: 14px; font-weight: bold;">${enrollment.PrimaryPhone || 'N/A'}</span>
                </div>
              </div>
              
              <div style="text-align: right; padding-left: 20px;">
                <div style="margin-bottom: 2px;">
                  <span style="font-size: 12px;">Classe: </span>
                  <span style="font-size: 12px; font-weight: bold;">${enrollment.Class || 'N/A'}</span>
                </div>
                <div>
                  <span style="font-size: 16px; font-weight: bold;">#Bus: </span>
                  <span style="font-size: 32px; font-weight: bold; line-height: 1;">${getBusNumber()}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div style="font-size: 12px; line-height: 1.4; margin-bottom: 5mm; min-height: 16mm;">
            ${getAddressDisplay()}
            ${getServiceNote() ? `<div style="font-size: 13px; color: #666; margin-top: 2mm; font-style: italic;">${getServiceNote()}</div>` : ''}
          </div>
          
          <div style="text-align: right; font-size: 13px; margin-top: 2mm;">
            الإدارة
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(printArea);

    return () => {
      const styleElement = document.getElementById('bus-card-print-styles');
      if (styleElement) {
        styleElement.remove();
      }
      const printElement = document.getElementById('bus-card-print-area');
      if (printElement) {
        printElement.remove();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollment]);

  const handlePrint = () => {
    window.print();
    if (onPrinted) {
      setTimeout(() => {
        const shouldMarkPrinted = window.confirm('Mark this card as printed?');
        if (shouldMarkPrinted) {
          onPrinted();
        }
      }, 500);
    }
  };

  const getAddress = () => {
    if (enrollment.UseCustomAddress) {
      return enrollment.MorningAddress || enrollment.EveningAddress;
    }
    return enrollment.FamilyAddress || enrollment.Address || '';
  };

  const getFullStudentName = () => {
    // Build: FirstName + FatherName (FatherName already includes family name)
    const parts = [];
    if (enrollment.FirstName) parts.push(enrollment.FirstName);
    if (enrollment.FatherName) parts.push(enrollment.FatherName);
    // Don't add LastName - it's already in FatherName
    return parts.filter(Boolean).join(' ');
  };

  const getBusNumber = () => {
    if (enrollment.BusService === 'MorningOnly') {
      return enrollment.MorningBusNumber;
    } else if (enrollment.BusService === 'EveningOnly') {
      return enrollment.EveningBusNumber;
    } else if (enrollment.HasDifferentBuses) {
      return `${enrollment.MorningBusNumber}/${enrollment.EveningBusNumber}`;
    }
    return enrollment.MorningBusNumber || enrollment.BusNumber;
  };

  const getServiceNote = () => {
    if (enrollment.BusService === 'MorningOnly') {
      return 'صباح فقط';
    } else if (enrollment.BusService === 'EveningOnly') {
      return 'مساء فقط';
    } else if (enrollment.HasDifferentBuses) {
      return `صباح: ${enrollment.MorningBusNumber} | مساء: ${enrollment.EveningBusNumber}`;
    }
    return '';
  };

  const getTrimesterDisplay = () => {
    const trimesterMap = {
      1: '1ère-Trimestre',
      2: '2ème-Trimestre',
      3: '3ème-Trimestre'
    };
    return trimesterMap[enrollment.Trimester] || `Trimestre ${enrollment.Trimester}`;
  };

  const getAcademicYearShort = () => {
    if (!enrollment.AcademicYear) return '';
    const year = enrollment.AcademicYear.toString();
    const parts = year.split('-');
    if (parts.length === 2) {
      if (parts[0].length === 2) {
        return year;
      }
      return `${parts[0].slice(2)}-${parts[1].slice(2)}`;
    }
    return year;
  };

  const getAddressDisplay = () => {
    if (enrollment.UseCustomAddress && enrollment.HasDifferentAddresses) {
      return `صباح: ${enrollment.MorningAddress} | مساء: ${enrollment.EveningAddress}`;
    }
    return getAddress();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Print Bus Card</h3>
            <p className="text-sm text-gray-600 mt-1">
              {enrollment.StudentName} - Bus #{getBusNumber()}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
            <p className="text-sm text-blue-800 font-semibold mb-2">📋 Printing Instructions:</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Page Orientation:</strong> LANDSCAPE (A4 horizontal)</li>
              <li>• <strong>Card Size:</strong> 13.3cm × 9cm</li>
              <li>• <strong>Position:</strong> RIGHT side of page (20mm from right edge)</li>
              <li>• Feed cardboard centered in tray</li>
              <li>• Click "Print Card" button when ready</li>
            </ul>
          </div>

          <div className="border-2 border-gray-300 rounded-lg bg-gray-100 p-8">
            <p className="text-sm text-gray-600 text-center mb-4">Print Preview (Landscape A4, Left-aligned):</p>

            <div
              className="bg-white border-2 border-gray-400"
              style={{
                width: '133mm',
                height: '90mm',
                padding: '8mm',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8mm'
              }}>
                <div style={{ flex: '1', textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
                    Collège des Apôtres
                  </p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
                    Jounieh
                  </p>
                </div>

                <div style={{ flex: '1', textAlign: 'center' }}>
                  <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                    Autocar
                  </p>
                </div>

                <div style={{ flex: '1', textAlign: 'center' }}>
                  {getAcademicYearShort() && (
                    <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
                      {getAcademicYearShort()}
                    </p>
                  )}
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
                    {getTrimesterDisplay()}
                  </p>
                </div>
              </div>

              <div style={{ 
                border: '2px solid black',
                padding: '6px 10px',
                marginBottom: '5mm'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px' }}>Élève: </span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {getFullStudentName()}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Tel: </span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {enrollment.PrimaryPhone || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right', paddingLeft: '20px' }}>
                    <div style={{ marginBottom: '2px' }}>
                      <span style={{ fontSize: '12px' }}>Classe: </span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {enrollment.Class || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>#Bus: </span>
                      <span style={{ fontSize: '32px', fontWeight: 'bold', lineHeight: 1 }}>
                        {getBusNumber()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ 
                fontSize: '13px',
                lineHeight: '1.4',
                marginBottom: '6mm',
                minHeight: '18mm'
              }}>
                {getAddressDisplay()}
                {getServiceNote() && (
                  <div style={{ 
                    fontSize: '13px',
                    color: '#666',
                    marginTop: '3mm',
                    fontStyle: 'italic'
                  }}>
                    {getServiceNote()}
                  </div>
                )}
              </div>

              <div style={{ 
                textAlign: 'right',
                fontSize: '14px',
                marginTop: '2mm'
              }}>
                الإدارة
              </div>
            </div>
          </div>

          {enrollment.HasDifferentBuses && (
            <div className="mt-4 bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r">
              <p className="text-sm text-purple-800 font-semibold">
                🚌 Different Buses: Morning #{enrollment.MorningBusNumber} → Evening #{enrollment.EveningBusNumber}
              </p>
              <p className="text-xs text-purple-600 mt-1">Card shows: {getBusNumber()}</p>
            </div>
          )}

          {enrollment.UseCustomAddress && enrollment.HasDifferentAddresses && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r">
              <p className="text-sm text-yellow-800 font-semibold mb-2">📍 Different Addresses</p>
              <div className="text-sm text-yellow-700 space-y-1">
                <p>🌅 Morning: {enrollment.MorningAddress}</p>
                <p>🌙 Evening: {enrollment.EveningAddress}</p>
              </div>
            </div>
          )}

          {enrollment.BusService !== 'Both' && enrollment.BusService && (
            <div className="mt-4 bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r">
              <p className="text-sm text-orange-800 font-semibold">
                {enrollment.BusService === 'MorningOnly' ? '🌅 Morning Only Service' : '🌙 Evening Only Service'}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center gap-2"
          >
            <Printer size={20} />
            Print Card
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusCardPrintModal;